import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, errMsg, money, CATEGORY_LABEL } from "@/lib/api";

const Summary = ({ order }) => {
  const rows = [
    ["Subtotal", order.subtotal, "order-subtotal"],
    order.discount > 0 && [`Discount${order.couponCode ? ` (${order.couponCode})` : ""}`, -order.discount, "order-discount"],
    ["Shipping", order.shippingFee, "order-shipping"],
    order.insuranceFee > 0 && ["Transit insurance", order.insuranceFee, "order-insurance"],
  ].filter(Boolean);
  return (
    <div className="space-y-2 border-t border-[#E5E0D8] pt-4 text-sm text-[#5A626A]">
      {rows.map(([label, v, id]) => (
        <div key={id} className="flex justify-between">
          <span>{label}</span>
          <span data-testid={id}>{v < 0 ? `−${money(-v)}` : v === 0 ? "Free" : money(v)}</span>
        </div>
      ))}
    </div>
  );
};

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!ref) {
      setErr("Missing order reference.");
      return;
    }
    let tries = 0;
    let timer;
    let cancelled = false;
    const check = () =>
      api
        .post(`/checkout/confirm/${ref}`)
        .then((r) => {
          if (cancelled) return;
          setOrder(r.data);
          if (r.data.status !== "Pending") clear();
          else if (++tries < 8) timer = setTimeout(check, 3000);
        })
        .catch((e) => !cancelled && setErr(errMsg(e)));
    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const paid = order && order.status !== "Pending";

  return (
    <div className="container-x max-w-3xl py-24 sm:py-32" data-testid="order-success-page">
      <p className="eyebrow">{paid ? "Order confirmed" : "Order"}</p>
      <h1 className="mt-6 font-display text-5xl font-light leading-tight sm:text-6xl" data-testid="order-status-heading">
        {err ? "We couldn't find that order." : !order ? "One moment…" : paid ? "Thank you." : "Awaiting payment confirmation"}
      </h1>
      {err && <p className="mt-6 text-sm text-[#5A626A]">{err}</p>}
      {order && (
        <>
          <p className="mt-8 text-[15px] leading-relaxed text-[#5A626A]">
            {paid
              ? `Your payment was received. ${order.receiptSent ? "A receipt has been sent to your email" : "Your receipt will be emailed to you shortly"}, and you'll receive tracking details as soon as your artwork is dispatched.`
              : "If you've just completed payment, this page will update in a few seconds. If you closed the payment page, you can return to your bag and try again."}
          </p>
          <p className="mt-6 text-sm">Reference <strong data-testid="order-ref">{order.ref}</strong></p>
          <div className="mt-12 space-y-4 border-t border-[#E5E0D8] pt-8">
            {order.items.map((i, n) => (
              <div key={n} className="flex justify-between text-sm">
                <span>{i.title} — {CATEGORY_LABEL[i.category]}{i.isOriginal ? "" : ` (${i.variantLabel} × ${i.quantity})`}</span>
                <span>{money(i.price * i.quantity)}</span>
              </div>
            ))}
            <Summary order={order} />
            <div className="flex justify-between border-t border-[#E5E0D8] pt-4 font-display text-2xl">
              <span>Total</span>
              <span data-testid="order-total">{money(order.totalAmount)}</span>
            </div>
          </div>
        </>
      )}
      <Link to="/gallery" className="link-u mt-14" data-testid="order-back-gallery">Return to the collection</Link>
    </div>
  );
}
